import { httpClient } from '@/api/http/client'
import { isHttpError } from '@/api/http/errors'
import type { AlbumSelection } from '@/lib/photos/album-selection'
import { sortAlbumDisplayNames } from '@/lib/photos/album-search'
import { sortStudentTags } from '@/lib/photos/student-tags'
import type {
  CandidAutoTaggingPayload,
  CandidAutoTaggingResponse,
  CandidDetail,
  CandidDetailResponse,
  PhotoAlbum,
  PhotoCandid,
  PhotoStudentTag,
  PhotoTopic,
  PhotosAlbumListResponse,
  PhotosAlbumResponse,
  PhotosTagsResponse,
  PhotosTopicsResponse,
} from '@/api/types/photo'
import type {
  CreatePhotoPezRequest,
  PhotoEffectCatalogEntryApi,
  PhotoPezResult,
  PhotosEffectsResponse,
  PhotosPezResponse,
} from '@/api/types/photo-effects'
import { normalizePhotoEffectsCatalogPayload } from '@/lib/photo-effects/photo-effect-catalog'
import { normalizePhotoPezPayload } from '@/lib/photo-effects/normalize-photo-pez-payload'

function parseAlbumsPayload(payload: PhotosAlbumListResponse['payload']): PhotoAlbum[] {
  if (Array.isArray(payload)) {
    return payload.filter(
      (item): item is PhotoAlbum => Boolean(item?.albumId) && typeof item.displayName === 'string'
    )
  }

  if (!payload || typeof payload !== 'object') return []

  const albums: PhotoAlbum[] = []

  for (const [key, value] of Object.entries(payload)) {
    if (!/^\d+$/.test(key) || !value || typeof value !== 'object') continue
    const record = value as Record<string, unknown>

    if (typeof record.albumId === 'string' && typeof record.displayName === 'string') {
      albums.push({
        albumId: record.albumId,
        displayName: record.displayName,
        candidCount: typeof record.candidCount === 'number' ? record.candidCount : undefined,
      })
    }
  }

  return albums
}

function readBooleanField(record: Record<string, unknown>, keys: string[]): boolean | undefined {
  for (const key of keys) {
    if (typeof record[key] === 'boolean') return record[key]
  }

  return undefined
}

function readStringField(record: Record<string, unknown>, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = record[key]

    if (typeof value === 'string' && value.length > 0) return value
  }

  return undefined
}

function readPositiveNumberField(
  record: Record<string, unknown>,
  keys: string[]
): number | undefined {
  for (const key of keys) {
    const value = record[key]

    if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value
  }

  return undefined
}

export function normalizePhotoCandid(raw: unknown): PhotoCandid | null {
  if (!raw || typeof raw !== 'object') return null

  const record = raw as Record<string, unknown>
  const candidId = readStringField(record, ['candidId', 'candidID', 'candid_id'])
  const displayName = readStringField(record, ['displayName', 'display_name'])
  const urlSqCrop = readStringField(record, ['urlSqCrop', 'sqCrop', 'url_sq_crop', 'sq_crop']) ?? ''
  const urlScr = readStringField(record, ['urlScr', 'url_scr'])
  const urlPreview = readStringField(record, ['urlPreview', 'url_preview'])
  const urlPri = readStringField(record, ['urlPri', 'url_pri'])

  if (!candidId || !displayName) return null

  const placedFlag = readBooleanField(record, ['placedFlag', 'placed', 'PlacedFlag']) ?? false
  const originalPlacedFlag =
    readBooleanField(record, ['originalPlacedFlag', 'originalPlaced', 'original_placed']) ??
    placedFlag
  const favoritedFlag =
    readBooleanField(record, ['favoritedFlag', 'favorited', 'FavoritedFlag']) ?? false
  const recommendedFlag =
    readBooleanField(record, ['recommendedFlag', 'recommended', 'RecommendedFlag']) ?? false

  const topicTags = Array.isArray(record.topicTags)
    ? record.topicTags.filter(
        (tag): tag is PhotoCandid['topicTags'][number] =>
          Boolean(tag) &&
          typeof tag === 'object' &&
          typeof (tag as { topicTagId?: unknown }).topicTagId === 'string' &&
          typeof (tag as { displayName?: unknown }).displayName === 'string'
      )
    : []

  const students = Array.isArray(record.students)
    ? record.students.filter(
        (student): student is NonNullable<PhotoCandid['students']>[number] =>
          Boolean(student) &&
          typeof student === 'object' &&
          typeof (student as { studentId?: unknown }).studentId === 'string'
      )
    : undefined

  const pixelWidth = readPositiveNumberField(record, ['pixelWidth', 'pixel_width'])
  const pixelHeight = readPositiveNumberField(record, ['pixelHeight', 'pixel_height'])
  const uploadDate = readPositiveNumberField(record, ['uploadDate', 'upload_date'])

  return {
    candidId,
    displayName,
    urlSqCrop,
    ...(urlScr ? { urlScr } : {}),
    ...(urlPreview ? { urlPreview } : {}),
    ...(urlPri ? { urlPri } : {}),
    placedFlag,
    originalPlacedFlag,
    favoritedFlag,
    recommendedFlag,
    ...(pixelWidth != null ? { pixelWidth } : {}),
    ...(pixelHeight != null ? { pixelHeight } : {}),
    ...(uploadDate != null ? { uploadDate } : {}),
    topicTags,
    students,
  }
}

async function getCandidPayload(
  path: string,
  options?: { timeoutMs?: number }
): Promise<PhotoCandid[]> {
  const response =
    options?.timeoutMs != null
      ? await httpClient.get<PhotosAlbumResponse>(path, { timeoutMs: options.timeoutMs })
      : await httpClient.get<PhotosAlbumResponse>(path)

  const { result, payload } = response

  if (!result || !Array.isArray(payload)) {
    throw new Error(`Failed to load photos from ${path}`)
  }

  return payload
    .map((item) => normalizePhotoCandid(item))
    .filter((item): item is PhotoCandid => item != null)
}

/** All-candids can be large; give the proxy more time than the default request. */
export const PHOTOS_CANDIDS_TIMEOUT_MS = 120_000

/** Picsart remove-background runs server-side; allow the same long timeout as all-candids. */
export const REMOVE_BACKGROUND_TIMEOUT_MS = 120_000

/** PEZ render can be slow on large candids. */
export const PHOTO_PEZ_TIMEOUT_MS = 120_000

export const photosService = {
  getTopics: async (): Promise<PhotoTopic[]> => {
    const { result, payload } = await httpClient.get<PhotosTopicsResponse>('/photos/topics')

    if (!result || !payload) {
      throw new Error('Failed to load topics')
    }

    const topics = Object.entries(payload)
      .filter(([key]) => /^\d+$/.test(key))
      .map(([, value]) => value as PhotoTopic)

    return topics.sort((a, b) =>
      a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' })
    )
  },

  getTags: async (): Promise<PhotoStudentTag[]> => {
    const { result, payload } = await httpClient.get<PhotosTagsResponse>('/photos/tags')

    if (!result || !Array.isArray(payload)) {
      throw new Error('Failed to load tags')
    }

    const active = payload.filter((tag) => tag.activeFlag !== false)

    return sortStudentTags(active)
  },

  getAlbums: async (): Promise<PhotoAlbum[]> => {
    const { result, payload } = await httpClient.get<PhotosAlbumListResponse>('/photos/albums')

    if (!result || payload == null) {
      throw new Error('Failed to load albums')
    }

    return sortAlbumDisplayNames(parseAlbumsPayload(payload))
  },

  getAlbumPhotos: async (albumId: string): Promise<PhotoCandid[]> => {
    return getCandidPayload(`/photos/albums/${encodeURIComponent(albumId)}/photos`)
  },

  getFavoritePhotos: async (): Promise<PhotoCandid[]> => {
    const photos = await getCandidPayload('/photos/favorites')

    // Favorites album photos are inherently favourited; ensure chip filter is a no-op.
    return photos.map((photo) => ({ ...photo, favoritedFlag: true }))
  },

  /** Personal recommendations (Recommend to me). */
  getRecommendToMePhotos: async (): Promise<PhotoCandid[]> => {
    return getCandidPayload('/photos/recommended')
  },

  /** Spread-scoped recommendations (Recommend for spread). */
  getRecommendedForSpreadPhotos: async (spreadId: string): Promise<PhotoCandid[]> => {
    return getCandidPayload(`/photos/recommended/spreads/${encodeURIComponent(spreadId)}`)
  },

  getSnapPhotos: async (): Promise<PhotoCandid[]> => {
    return getCandidPayload('/photos/snap')
  },

  /** All account candids (All photos tray). No server pagination — FE windows the list. */
  getAllPhotos: async (): Promise<PhotoCandid[]> => {
    return getCandidPayload('/photos/candids', { timeoutMs: PHOTOS_CANDIDS_TIMEOUT_MS })
  },

  getCandidAutoTagging: async (candidId: string): Promise<CandidAutoTaggingPayload> => {
    const { result, payload } = await httpClient.get<CandidAutoTaggingResponse>(
      `/candid/autotagging/${encodeURIComponent(candidId)}`
    )

    if (!result || !payload) {
      throw new Error(`Failed to load auto-tagging data for ${candidId}`)
    }

    return payload
  },

  getCandidDetail: async (candidId: string): Promise<CandidDetail> => {
    const { result, payload } = await httpClient.get<CandidDetailResponse>(
      `/candid/detail/${encodeURIComponent(candidId)}`
    )

    if (!result || !payload) {
      throw new Error(`Failed to load candid detail for ${candidId}`)
    }

    return payload
  },

  deletePhotoRecommendation: async (candidId: string, staffId: string): Promise<void> => {
    const { result } = await httpClient.post<
      { candidId: string; staffId: string },
      { result?: boolean }
    >('/photos/delete-recommendation', { candidId, staffId })

    if (result === false) {
      throw new Error('Failed to remove photo from recommendations')
    }
  },

  /**
   * POST /photos/candids/{candidId}/remove-background
   * Backend calls Picsart and returns the newly created edited candid (`*_Edited` filename).
   */
  removeBackground: async (candidId: string): Promise<PhotoCandid> => {
    let result: boolean | undefined
    let payload: unknown

    try {
      ;({ result, payload } = await httpClient.post<
        undefined,
        { result?: boolean; payload?: unknown }
      >(`/photos/candids/${encodeURIComponent(candidId)}/remove-background`, undefined, {
        timeoutMs: REMOVE_BACKGROUND_TIMEOUT_MS,
      }))
    } catch (error) {
      if (isHttpError(error) && error.response) {
        const body = (await error.response.json().catch(() => null)) as { error?: unknown } | null
        const apiError = typeof body?.error === 'string' ? body.error.trim() : ''

        if (apiError) {
          throw new Error(apiError)
        }
      }

      throw new Error(error instanceof Error ? error.message : 'Failed to remove background')
    }

    const photo = normalizePhotoCandid(Array.isArray(payload) ? payload[0] : payload)

    if (!result || !photo) {
      throw new Error('Failed to remove background')
    }

    return photo
  },

  searchPhotosByFilename: async (filename: string): Promise<PhotoCandid[]> => {
    const trimmed = filename.trim()

    if (!trimmed) return []

    return getCandidPayload(`/photos/search?filename=${encodeURIComponent(trimmed)}`)
  },

  getPhotoEffectsCatalog: async (): Promise<PhotoEffectCatalogEntryApi[]> => {
    const { result, payload } = await httpClient.get<PhotosEffectsResponse>('/photos/effects')

    if (!result) {
      throw new Error('Failed to load photo effects catalog')
    }

    return normalizePhotoEffectsCatalogPayload(payload)
  },

  createPhotoPez: async (body: CreatePhotoPezRequest): Promise<PhotoPezResult> => {
    const { result, payload } = await httpClient.post<CreatePhotoPezRequest, PhotosPezResponse>(
      '/photos/pez',
      body,
      { timeoutMs: PHOTO_PEZ_TIMEOUT_MS }
    )

    if (!result) {
      throw new Error('Failed to apply photo effects')
    }

    return normalizePhotoPezPayload(payload)
  },

  getPhotoPez: async (pezId: string): Promise<PhotoPezResult> => {
    const { result, payload } = await httpClient.get<PhotosPezResponse>(
      `/photos/pez/${encodeURIComponent(pezId)}`,
      { timeoutMs: PHOTO_PEZ_TIMEOUT_MS }
    )

    if (!result) {
      throw new Error('Failed to load photo effects')
    }

    return normalizePhotoPezPayload(payload)
  },

  getPhotosForSelection: async (
    selection: AlbumSelection,
    options?: { spreadId?: string | null }
  ): Promise<PhotoCandid[]> => {
    if (selection.type === 'album') {
      return photosService.getAlbumPhotos(selection.albumId)
    }

    switch (selection.key) {
      case 'all-photos':
        return photosService.getAllPhotos()
      case 'favorites':
        return photosService.getFavoritePhotos()
      case 'recommended': {
        const spreadId = options?.spreadId

        if (!spreadId) {
          throw new Error('Spread ID is required for Recommend for spread')
        }

        return photosService.getRecommendedForSpreadPhotos(spreadId)
      }

      case 'recommend-to-me':
        return photosService.getRecommendToMePhotos()
      case 'snap':
        return photosService.getSnapPhotos()
    }
  },
}
